/**
 * Wallet connect panel: Grudge ID (who) + Phantom (sign) + play ledger (tables).
 */
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useWalletState } from "@/contexts/wallet-context";
import { openGrudgeWalletSite, isLoggedIn } from "@/lib/grudge-auth";
import { installUrl, type SolanaWalletId } from "@/lib/solana-wallets";
import { BuyGbuxModal } from "@/components/buy-gbux-modal";
import { ExternalLink, LogIn, Wallet, ArrowDownToLine, Link2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { GBUX, FLEET } from "@shared/fleet";

export function WalletConnectPanel() {
  const {
    wallet,
    connect,
    disconnect,
    selectWallet,
    forgetWallet,
    refreshMultiWalletBalances,
    linkToGrudgeAccount,
    loginGrudgeId,
    detectedWallets,
    rescanWallets,
    refreshBalances,
    openWalletPanel,
  } = useWalletState();
  const { toast } = useToast();
  const [buyOpen, setBuyOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    rescanWallets();
    void refreshMultiWalletBalances();
  }, [rescanWallets, refreshMultiWalletBalances]);

  const onConnect = async (id: SolanaWalletId) => {
    setBusy(true);
    try {
      await connect(id);
      toast({ title: "Wallet connected", description: id });
      if (isLoggedIn()) {
        try {
          await linkToGrudgeAccount();
          toast({ title: "Linked to Grudge account" });
        } catch (e) {
          toast({
            title: "Connected (not linked)",
            description:
              e instanceof Error ? e.message : "Sign in to link for GBUX buys",
          });
        }
      }
      await refreshBalances();
    } catch (e) {
      toast({
        title: "Connect failed",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950/90 p-4 space-y-4 text-zinc-100">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-semibold">
          <Wallet className="h-4 w-4 text-amber-400" />
          Play
        </div>
        <span className="text-[10px] text-emerald-500/90 uppercase tracking-wide font-semibold">
          {GBUX.symbol} · mainnet
        </span>
      </div>

      <p className="text-[11px] text-zinc-500 leading-relaxed">
        Grudge ID is your account. Phantom signs. Play ledger sits the table.
      </p>

      <div className="grid gap-2">
        {!wallet.isAuthenticated ? (
          <Button
            className="w-full bg-violet-700 hover:bg-violet-600"
            onClick={() => loginGrudgeId("/account")}
          >
            <LogIn className="mr-2 h-4 w-4" />
            Sign in with Grudge ID
          </Button>
        ) : (
          <p className="text-xs text-emerald-400">
            Grudge ID session active
            {wallet.custodialWallet
              ? ` · fleet ${wallet.custodialWallet.slice(0, 4)}…${wallet.custodialWallet.slice(-4)}`
              : ""}
          </p>
        )}

        <Button
          className="w-full bg-amber-600 hover:bg-amber-500 font-semibold"
          onClick={() => openWalletPanel("transfer")}
        >
          <ArrowDownToLine className="mr-2 h-4 w-4" />
          Fund play (sign transfer)
        </Button>

        <Button
          variant="outline"
          className="w-full border-zinc-700"
          onClick={() => openGrudgeWalletSite()}
        >
          Studio bag
          <ExternalLink className="ml-2 h-3.5 w-3.5" />
        </Button>
        {wallet.connected && wallet.publicKey && (
          <div className="rounded-lg border border-zinc-800 bg-black/40 px-3 py-2 text-[11px] text-zinc-400 space-y-1">
            <div className="flex justify-between gap-2">
              <span>Play ledger</span>
              <strong className="text-emerald-300">
                {Number(wallet.playGbux || 0).toLocaleString()} GBUX
              </strong>
            </div>
            <div className="flex justify-between gap-2">
              <span>Wallet (on-chain)</span>
              <span className="text-cyan-200">
                {Number(wallet.chainGbux || 0).toLocaleString()} GBUX ·{" "}
                {Number(wallet.chainSol || wallet.solBalance || 0).toFixed(4)} SOL
              </span>
            </div>
            {wallet.accountGbux > 0 && (
              <div className="flex justify-between gap-2">
                <span>Fleet bag</span>
                <span className="text-amber-200">
                  {Number(wallet.accountGbux).toLocaleString()} GBUX
                </span>
              </div>
            )}
            <p className="text-[10px] text-zinc-600 pt-1">
              mainnet · mint {GBUX.mint.slice(0, 6)}… · transfer signs in wallet
              extension · {FLEET.walletSite.replace("https://", "")}
            </p>
          </div>
        )}

        {/* Multi-wallet registry — public addresses + cached balances only */}
        {(wallet.multiWallets?.length ?? 0) > 0 && (
          <div className="rounded-lg border border-violet-500/25 bg-violet-950/20 px-3 py-2 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-violet-200/90">
                Multi-wallet ({wallet.multiWallets.length})
              </p>
              <button
                type="button"
                className="text-[10px] text-violet-300/80 underline"
                onClick={() => void refreshMultiWalletBalances()}
              >
                Refresh
              </button>
            </div>
            <ul className="space-y-1.5 max-h-40 overflow-y-auto">
              {wallet.multiWallets.map((w) => {
                const active = w.address === wallet.publicKey;
                return (
                  <li
                    key={w.address}
                    className={`flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-[11px] ${
                      active
                        ? "bg-violet-600/30 border border-violet-400/40"
                        : "bg-black/30 border border-transparent"
                    }`}
                  >
                    <button
                      type="button"
                      className="text-left min-w-0 flex-1"
                      onClick={() => void selectWallet(w.address)}
                    >
                      <div className="font-mono text-zinc-200 truncate">
                        {w.address.slice(0, 4)}…{w.address.slice(-4)}
                        {active ? " · active" : ""}
                      </div>
                      <div className="text-[10px] text-zinc-500 truncate">
                        {w.label}
                        {w.playGbux != null
                          ? ` · play ${Number(w.playGbux).toLocaleString()}`
                          : ""}
                        {w.chainGbux != null
                          ? ` · chain ${Number(w.chainGbux).toLocaleString()}`
                          : ""}
                      </div>
                    </button>
                    <button
                      type="button"
                      className="text-[10px] text-zinc-500 hover:text-red-300 shrink-0"
                      title="Forget from this browser"
                      onClick={() => forgetWallet(w.address)}
                    >
                      ×
                    </button>
                  </li>
                );
              })}
            </ul>
            <p className="text-[9px] text-zinc-600 leading-relaxed">
              Local public keys only — no secrets. Play GBUX is per address on edge
              D1. Switch active before fund / sit.
            </p>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <p className="text-xs text-zinc-500">
          Phantom embed (all accounts / Google / Apple) · Solana mainnet
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Button
            size="sm"
            className="bg-violet-700 hover:bg-violet-600"
            disabled={busy}
            onClick={() => void onConnect("phantom")}
          >
            👻 Phantom inject
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await connect("phantom", { phantomMode: "google" });
                toast({ title: "Phantom (Google) connected" });
                await refreshBalances();
              } catch (e) {
                toast({
                  title: "Phantom Google failed",
                  description: e instanceof Error ? e.message : String(e),
                  variant: "destructive",
                });
              } finally {
                setBusy(false);
              }
            }}
          >
            Phantom · Google
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await connect("phantom", { phantomMode: "apple" });
                toast({ title: "Phantom (Apple) connected" });
                await refreshBalances();
              } catch (e) {
                toast({
                  title: "Phantom Apple failed",
                  description: e instanceof Error ? e.message : String(e),
                  variant: "destructive",
                });
              } finally {
                setBusy(false);
              }
            }}
          >
            Phantom · Apple
          </Button>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="w-full border-zinc-700 text-zinc-300"
          onClick={() => openGrudgeWalletSite()}
        >
          Studio bag (Railway)
          <ExternalLink className="ml-2 h-3 w-3" />
        </Button>
      </div>

      <div>
        <p className="text-xs text-zinc-500 mb-2">
          All Solana inject wallets (opt-in) — available first
        </p>
        <div className="grid grid-cols-2 gap-2">
          {detectedWallets
            .filter((w) => w.id !== "generic")
            .map((w) => (
              <Button
                key={w.id}
                size="sm"
                variant={w.available ? "secondary" : "ghost"}
                disabled={busy}
                className="justify-start"
                onClick={() => {
                  if (!w.available) {
                    window.open(installUrl(w.id), "_blank");
                    return;
                  }
                  void onConnect(w.id);
                }}
              >
                <span className="mr-1">{w.icon}</span>
                {w.name}
                {!w.available && (
                  <span className="ml-auto text-[10px] opacity-60">install</span>
                )}
              </Button>
            ))}
        </div>
        <p className="text-[10px] text-zinc-600 mt-2 leading-relaxed">
          On-chain SOL/GBUX stay in each wallet until you fund play. Play ledger is
          per address on D1. Multi-wallet switch above · scopes recheck on login.
        </p>
      </div>

      {wallet.connected && wallet.publicKey && (
        <div className="rounded-lg bg-zinc-900/80 p-3 text-sm space-y-1">
          <p className="font-mono text-xs truncate">
            {wallet.walletType}: {wallet.publicKey}
          </p>
          <p>
            Play GBUX:{" "}
            <strong className="text-amber-300">
              {(wallet.playGbux || wallet.accountGbux || wallet.gbuxBalance).toLocaleString()}
            </strong>
          </p>
          {wallet.custodialWallet && (
            <p className="text-xs text-zinc-500 truncate">
              Custodial: {wallet.custodialWallet}
            </p>
          )}
          <div className="flex flex-wrap gap-2 pt-2">
            <Button size="sm" onClick={() => setBuyOpen(true)}>
              Buy GBUX
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-zinc-700"
              disabled={!wallet.isAuthenticated}
              onClick={async () => {
                try {
                  await linkToGrudgeAccount();
                  toast({ title: "Wallet linked" });
                } catch (e) {
                  toast({
                    title: "Link failed",
                    description: e instanceof Error ? e.message : String(e),
                    variant: "destructive",
                  });
                }
              }}
            >
              <Link2 className="mr-1 h-3 w-3" />
              Link to account
            </Button>
            <Button size="sm" variant="ghost" onClick={() => void disconnect()}>
              Disconnect
            </Button>
          </div>
        </div>
      )}

      <p className="text-[10px] text-zinc-600">
        Fleet: {FLEET.walletSite.replace("https://", "")} · account bag GBUX on
        Railway · never localStorage-only for production chips.
      </p>

      <BuyGbuxModal open={buyOpen} onOpenChange={setBuyOpen} />
    </div>
  );
}
